import { useElectronicAddresses } from '@/queries/electronic_addresses'
import { DirectoryAddress, SharedProps } from '@/types'
import { usePage } from '@inertiajs/react'
import { Alert, Badge, Button, Group, Loader, Stack, Text } from '@mantine/core'
import { IconAddressBook } from '@tabler/icons-react'

function AddressOption({
  address,
  selected,
  onSelect,
}: {
  address: DirectoryAddress
  selected: boolean
  onSelect: (identifier: string) => void
}) {
  const handleSelect = () => onSelect(address.identifier)

  return (
    <Group justify="space-between" wrap="nowrap">
      <Group gap="xs" wrap="nowrap">
        <Text size="sm" ff="monospace">
          {address.identifier}
        </Text>
        {!address.active && (
          <Badge size="xs" color="gray" variant="light">
            Inactive
          </Badge>
        )}
      </Group>
      <Button size="compact-xs" variant={selected ? 'filled' : 'light'} disabled={!address.active} onClick={handleSelect}>
        {selected ? 'Choisie' : 'Utiliser'}
      </Button>
    </Group>
  )
}

// Looks the société's SIREN up in the platforms' directory, where each company declares where it receives its
// invoices, so the address is picked rather than typed.
export default function ElectronicAddressPicker({
  siren,
  value,
  onSelect,
}: {
  siren: string
  value: string
  onSelect: (identifier: string) => void
}) {
  const { current_organization } = usePage<SharedProps>().props
  const { data: directory, isFetching, isError } = useElectronicAddresses({
    organizationSlug: current_organization!.slug,
    siren,
  })

  if (isFetching) return <Loader size="xs" />
  if (isError) {
    return (
      <Text size="xs" c="dimmed">
        L'annuaire des plateformes est injoignable pour l'instant.
      </Text>
    )
  }
  if (!directory?.connected) return null

  if (directory.entries.length === 0) {
    return (
      <Alert variant="light" color="yellow" icon={<IconAddressBook />} p="xs">
        Aucune adresse dans l'annuaire pour ce SIREN : la société n'a peut-être pas encore choisi sa plateforme de
        réception. Demandez-lui son adresse de facturation électronique.
      </Alert>
    )
  }

  return (
    <Stack gap={6}>
      <Text size="xs" c="dimmed">
        Adresses déclarées dans l'annuaire des plateformes
      </Text>
      {directory.entries.map((address) => (
        <AddressOption
          key={address.identifier}
          address={address}
          selected={address.identifier === value}
          onSelect={onSelect}
        />
      ))}
    </Stack>
  )
}
